import Button from '@components/atoms/button'
import CustomPhoneInput from '@components/book-trial-class/phone-input'
import { GRADES, GradeType } from '@components/common/forms/grade-select'
import LazyRecaptcha from '@components/common/lazy-recaptcha'
import Modal from '@components/common/modal'
import { CClient } from '@common/utils'
import { requestLoginOtp } from '@containers/auth/utils'
import { VerifyPhoneOtpContainer } from '@containers/auth/verify-otp'
import { useSessionContext } from '@contexts/session'
import {
  generateUTMParametersDictFromCookie,
  getClickIDParametersDictFromCookie,
  getUTMParametersDictFromLocalStorage,
} from '@lib/analytics/track-utm'
import { CourseType } from '@lib/data/summer-coding-camp'
import useDetectCountry from '@hooks/useDetectCountry'
import useLazyRecaptcha from '@hooks/useLazyRecaptcha'
import { SYSTEM_TIMEZONE } from '@lib/utils/date-native'
import * as Sentry from '@sentry/node'
import tc from 'countries-and-timezones'
import { useEffect, useMemo, useState } from 'react'
import { Check } from 'react-feather'

// The camp pages pass a marketing coupon whose trailing number is the discount
// percent (e.g. SUMMER50 -> 50%, WINTER33 -> 33%). Deriving the fraction from
// the code keeps it in sync with whatever coupon a page uses, instead of a
// hardcoded map that drifts. The same fraction is sent to the pricing API and
// the camp enrolment endpoint so the displayed price == the charged amount.
const getDiscountFraction = (coupon: string): number => {
  const match = (coupon || '').match(/(\d{1,2})\s*$/)
  if (!match) return 0
  const percent = Number(match[1])
  return percent > 0 && percent < 100 ? percent / 100 : 0
}

// All time zones for the step 1 dropdown, sourced from the same
// `countries-and-timezones` library used by the "Update your timezone" modal
// (components/trial-class/timezone-modal.tsx). Sorted by offset, labelled
// "(GMT ±offset) Name".
const TIMEZONE_OPTIONS = Object.values(tc.getAllTimezones())
  .sort((a, b) => a.dstOffset - b.dstOffset)
  .map((timezone) => ({
    label: `(GMT ${timezone.dstOffsetStr}) ${timezone.name}`,
    value: timezone.name,
  }))

// Anonymous users go through Schedule -> Details -> Payment. Logged-in users
// already have an account, so they skip the Details step.
const STEPS_GUEST = ['Schedule', 'Details', 'Payment']
const STEPS_LOGGED_IN = ['Schedule', 'Payment']

// Class time slots in 15-minute steps from 6:00 AM to 10:00 PM (inclusive).
const TIME_SLOT_START_HOUR = 6
const TIME_SLOT_END_HOUR = 22
const TIME_OPTIONS = Array.from(
  { length: (TIME_SLOT_END_HOUR - TIME_SLOT_START_HOUR) * 4 + 1 },
  (_, i) => {
    const hour = TIME_SLOT_START_HOUR + Math.floor(i / 4)
    const minute = (i % 4) * 15
    const value = `${String(hour).padStart(2, '0')}:${String(minute).padStart(
      2,
      '0'
    )}`
    const period = hour < 12 ? 'AM' : 'PM'
    const hour12 = hour % 12 || 12
    const label = `${hour12}:${String(minute).padStart(2, '0')} ${period}`
    return { value, label }
  }
)

type Props = {
  course: CourseType | null
  couponCode: string
  isOpen: boolean
  setIsOpen: (open: boolean) => void
}

// Earliest selectable start date: tomorrow. The current date (and past dates)
// cannot be selected.
const minBookingDate = () => {
  const d = new Date()
  d.setDate(d.getDate() + 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
    2,
    '0'
  )}-${String(d.getDate()).padStart(2, '0')}`
}

const Stepper = ({ step, steps }: { step: number; steps: string[] }) => (
  <div className="flex items-center justify-center pb-6">
    {steps.map((label, i) => {
      const n = i + 1
      const isDone = n < step
      const isActive = n === step
      return (
        <div key={label} className="flex items-center">
          <div className="flex flex-col items-center">
            <div
              className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-700 ${
                isDone || isActive
                  ? 'bg-orange text-white'
                  : 'bg-white text-grey-700 border border-grey-400'
              }`}
            >
              {isDone ? <Check size={16} strokeWidth={3} /> : n}
            </div>
            <span
              className={`mt-1 text-xs font-600 ${
                isDone || isActive ? 'text-orange' : 'text-grey-700'
              }`}
            >
              {label}
            </span>
          </div>
          {n < steps.length && (
            <div
              className={`w-10 sm:w-16 h-0.5 mx-2 mb-5 ${
                n < step ? 'bg-orange' : 'bg-grey-300'
              }`}
            />
          )}
        </div>
      )
    })}
  </div>
)

const BookingModal = ({ course, couponCode, isOpen, setIsOpen }: Props) => {
  const { countryCode, currency } = useDetectCountry()
  const session = useSessionContext()
  const isLoggedIn = !!session?.loggedIn

  const steps = isLoggedIn ? STEPS_LOGGED_IN : STEPS_GUEST

  const [step, setStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Step 1 - schedule. Logged-in users default to their saved timezone.
  const [startDate, setStartDate] = useState('')
  const [classTime, setClassTime] = useState('')
  const [timezone, setTimezone] = useState(session?.timezone || SYSTEM_TIMEZONE)

  // Step 2 - details
  const [phone, setPhone] = useState('')
  const [grade, setGrade] = useState<GradeType>({ value: '', label: '' })

  // When the phone already has an account, we switch step 2 to an OTP login
  // sub-step (reusing the shared VerifyPhoneOtpContainer) instead of erroring.
  const [showOtp, setShowOtp] = useState(false)

  // Invisible reCAPTCHA (required by the user registration endpoint).
  const {
    recaptchaAPI,
    setRecaptchaAPI,
    shouldRecaptchaLoad,
    setShouldRecaptchaLoad,
  } = useLazyRecaptcha()

  // Pricing - original (struck-through) total + the discounted amount payable.
  const [price, setPrice] = useState<{
    total: number
    discounted: number
    currency: string
  } | null>(null)

  // Payment step - gateway links returned by the camp-enrolment endpoint, plus
  // the hosted payment page URL used as a fallback when no inline links exist.
  const [paymentLinks, setPaymentLinks] = useState<Record<string, string>>({})
  const [paymentPageUrl, setPaymentPageUrl] = useState('')
  const [paymentUuid, setPaymentUuid] = useState('')
  // Stripe checkout-session generation in-flight, to avoid double clicks.
  const [checkoutLoading, setCheckoutLoading] = useState(false)

  // The payment step is the last step (3 for guests, 2 for logged-in users).
  const paymentStep = steps.length

  const discountPercent = getDiscountFraction(couponCode)

  // Reset to step 1 whenever a new course is opened, defaulting the timezone
  // to the logged-in user's saved timezone (else the system zone).
  useEffect(() => {
    if (isOpen) {
      setStep(1)
      setError('')
      setShowOtp(false)
      setTimezone(session?.timezone || SYSTEM_TIMEZONE)
    }
  }, [isOpen, course, session?.timezone])

  // Load the reCAPTCHA widget once the user reaches the details step.
  useEffect(() => {
    if (step === 2) setShouldRecaptchaLoad(true)
  }, [step, setShouldRecaptchaLoad])

  // Fetch the localized price for the selected course.
  useEffect(() => {
    if (!isOpen || !course?.courseName || !countryCode) return
    const fetchPrice = async () => {
      try {
        const response = await CClient(
          '/api/v1/pricing/calculate-course-pricing/',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              country_code: countryCode,
              currency_code: currency || 'USD',
              course_name: course.courseName,
              subject: 'Coding',
              service_type: 'Codingal Prime',
              monthly_classes: 6,
              enrollment_type: 'New Sale',
              discount_percent: discountPercent,
            }),
          }
        )
        if (response.status === 200) {
          const data = await response.json()
          const pricing = data.pricing || {}
          const total = pricing.totalFee ?? null
          if (total != null) {
            // Prefer the discounted total from the API if it applied the
            // discount; otherwise derive it from the frontend discount percent
            // (coupon codes are hardcoded on the frontend).
            const discounted =
              pricing.discountedTotalFee != null
                ? Number(pricing.discountedTotalFee)
                : Math.round(Number(total) * (1 - discountPercent))
            setPrice({
              total: Number(total),
              discounted,
              currency: data.parameters?.currencyCode || currency || 'USD',
            })
          }
        }
      } catch (e) {
        Sentry.captureException(e)
      }
    }
    fetchPrice()
  }, [isOpen, course, countryCode, currency, discountPercent])

  // Formatted price strings: the discounted amount payable and the original
  // (struck-through) total. `hasDiscount` is true only when they differ.
  const priceInfo = useMemo(() => {
    if (!price) return null
    const fmt = (n: number) => `${price.currency} ${n.toLocaleString()}`
    return {
      discounted: fmt(price.discounted),
      total: fmt(price.total),
      hasDiscount: price.discounted < price.total,
    }
  }, [price])

  const weekRange = useMemo(() => {
    if (!startDate) return ''
    const start = new Date(startDate)
    const end = new Date(start.getTime() + 6 * 86400000)
    const fmt = (d: Date) =>
      d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    return `${fmt(start)} - ${fmt(end)}, ${start.getFullYear()}`
  }, [startDate])

  const startDateLabel = useMemo(() => {
    if (!startDate) return ''
    return new Date(startDate).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }, [startDate])

  // 12-hour formatted class time (e.g. "10:00 AM") for display.
  const classTimeLabel = useMemo(() => {
    const slot = TIME_OPTIONS.find((s) => s.value === classTime)
    return slot ? slot.label : classTime
  }, [classTime])

  const canProceedStep1 = !!startDate && !!classTime
  const canProceedStep2 = phone.length >= 10 && !!grade.value

  // Create the camp enrolment + payment page for the (now logged-in) user and
  // redirect to the payment page. Shared by the guest and logged-in flows.
  const createEnrolmentAndShowPayment = async () => {
    const enrolmentResponse = await CClient('/api/v1/camp-enrolment/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        course_code: course?.courseCode,
        day: startDate,
        time: classTime,
        discount_code: couponCode,
        discount_percent: discountPercent,
      }),
    })

    if (enrolmentResponse.status >= 400) {
      // Surface the backend's specific message (e.g. existing enrolment) when
      // available, else a generic fallback.
      const body = await enrolmentResponse.json().catch(() => ({}))
      setError(
        body?.message || 'Could not start your payment. Please try again.'
      )
      setLoading(false)
      return
    }

    const enrolmentData = await enrolmentResponse.json()
    const links = enrolmentData.paymentLinks || {}
    const pageUrl = enrolmentData.paymentLink || ''

    // Need at least one inline gateway link or the hosted page URL to continue.
    if (!Object.keys(links).length && !pageUrl) {
      setError('Could not start your payment. Please try again.')
      setLoading(false)
      return
    }

    // Show the payment options inline as the final step. If no inline gateway
    // links are available, the hosted payment page is offered as a fallback.
    setPaymentLinks(links)
    setPaymentPageUrl(pageUrl)
    setPaymentUuid(enrolmentData.uuid || '')
    setStep(paymentStep)
    setLoading(false)
  }

  // For Stripe, generate a Checkout Session (checkout.stripe.com) instead of
  // using the stored hosted-invoice link - same UX as the payment page. If the
  // session can't be generated, fall back to the hosted payment page
  // (/payment/{uuid}/), which handles checkout, gateways and its own fallbacks.
  const handleStripeCheckout = async () => {
    const fallbackUrl = paymentPageUrl
    if (!paymentUuid) {
      window.location.href = fallbackUrl
      return
    }
    setCheckoutLoading(true)
    try {
      const response = await CClient('/api/v1/stripe-checkout-session/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uuid: paymentUuid }),
      })
      if (response.status === 200) {
        const data = await response.json()
        window.location.href = data?.sessionLink || fallbackUrl
        return
      }
      window.location.href = fallbackUrl
    } catch (e) {
      Sentry.captureException(e)
      window.location.href = fallbackUrl
    }
  }

  // Step 1 "Proceed" for logged-in users: skip the details step. Update the
  // timezone on the user if it was changed, then create the enrolment.
  const handleLoggedInProceed = async () => {
    setError('')
    setLoading(true)
    try {
      if (timezone && timezone !== session?.timezone) {
        await CClient('/api/v1/users/me/', {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ timezone }),
        })
      }
      await createEnrolmentAndShowPayment()
    } catch (e) {
      Sentry.captureException(e)
      setError('Something went wrong. Please try again.')
      setLoading(false)
    }
  }

  // Step 1 "Proceed" dispatcher.
  const handleStep1Proceed = () => {
    if (isLoggedIn) {
      handleLoggedInProceed()
    } else {
      setStep(2)
    }
  }

  // Triggers the invisible reCAPTCHA. The actual registration runs in
  // handleRecaptchaOnChange once the token is available.
  const handleStep2Submit = async () => {
    setError('')
    if (!recaptchaAPI) {
      setError('Verification is still loading. Please try again in a moment.')
      return
    }
    setLoading(true)
    try {
      await recaptchaAPI.execute()
    } catch (e) {
      Sentry.captureException(e)
      setError('Something went wrong. Please try again.')
      setLoading(false)
    }
  }

  const handleRecaptchaOnChange = async (recaptchaKey: string) => {
    // onChange also fires on reset with an empty key - ignore that.
    if (!recaptchaKey) return
    recaptchaAPI?.reset()

    // This body is hand-rolled rather than built by
    // `prepareRegistrationFormBody`, so it was silently registering camp
    // signups with no attribution at all - losing every utm_* param and both
    // click IDs. Attach the lead source the same way every other signup path
    // does. (Reusing the shared builder outright would also flip `getOtp`,
    // `hasLaptop` and `signupSource` for this flow, which is a bigger change
    // than the missing attribution warrants.)
    let leadSourceDict = getUTMParametersDictFromLocalStorage()
    if (!leadSourceDict) leadSourceDict = generateUTMParametersDictFromCookie()
    const clickIDs = getClickIDParametersDictFromCookie()
    // Only send the key when there is something to send. An empty object
    // would still create a blank LeadSource row, and an absent `lead_source`
    // is what `multiverse.is_organic_or_high_value_lead` and
    // `get_cna_recommendation` read as organic - so attaching a blank row
    // would quietly move attribution-less camp leads out of the solo-trial /
    // CNA treatment. Omitting the key keeps that path byte-identical.
    const leadSource =
      leadSourceDict || clickIDs ? { ...leadSourceDict, ...clickIDs } : null

    try {
      // 1. Register + log in the user (token cookie set server-side).
      const registerResponse = await CClient('/api/v1/users/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          // The camp form only collects phone + grade. Name/email are required
          // by the registration endpoint, so we send the same placeholders the
          // book-trial signup flow uses for phone-only signups.
          name: 'Student',
          email: `${phone}@codingal-student.com`,
          phone: `+${phone}`,
          country: countryCode,
          timezone,
          student: { grade: grade.value },
          signup_source: '/camp/',
          ...(leadSource && { leadSource }),
          recaptcha_response: recaptchaKey,
        }),
      })

      // If the phone already has an account, the unique constraint fails.
      // Switch to OTP login (reusing the shared OTP modal) and, once verified,
      // continue to payment - instead of dead-ending with an error.
      if (registerResponse.status >= 400) {
        const body = await registerResponse.json().catch(() => ({}))
        const phoneTaken =
          JSON.stringify(body).toLowerCase().includes('phone') ||
          registerResponse.status === 409
        if (phoneTaken) {
          const otpResponse = await requestLoginOtp({ phone: `+${phone}` })
          if (otpResponse.status === 200) {
            setShowOtp(true)
          } else {
            setError(
              'An account with this phone already exists, but we could not send an OTP. Please try again.'
            )
          }
        } else {
          setError('Could not register. Please check your details and retry.')
        }
        setLoading(false)
        return
      }

      // 2. Create the camp enrolment + payment page, then redirect.
      await createEnrolmentAndShowPayment()
    } catch (e) {
      Sentry.captureException(e)
      setError('Something went wrong. Please try again.')
      setLoading(false)
    }
  }

  if (!course) return null

  return (
    <>
      {/* reCAPTCHA is rendered outside the modal so its badge / challenge never
          affects the modal layout. Pinned to the right of the screen, above the
          modal overlay. */}
      <div className="fixed top-0 right-0 z-[200]">
        <LazyRecaptcha
          shouldRecaptchaLoad={shouldRecaptchaLoad}
          onChange={handleRecaptchaOnChange}
          setRecaptchaAPI={setRecaptchaAPI}
        />
      </div>

      <Modal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        isCrossIconShown
        width="sm:max-w-md"
        shouldCloseOnEsc
      >
        <div className="text-grey">
          {/* Selected course chip (no price - pricing is shown on the payment
              step only). */}
          <div className="p-3 mb-4 border rounded-lg bg-orange-100 bg-opacity-40 border-orange-200">
            <p className="font-700">{course.curriculumCard?.title}</p>
            <p className="text-sm text-grey-900">
              Ages {course.age} · {course.sessions} sessions
            </p>
          </div>

          <Stepper step={step} steps={steps} />

          {/* STEP 1 - SCHEDULE */}
          {step === 1 && (
            <div>
              <h3 className="mb-1 text-lg font-700">Pick your camp schedule</h3>
              <p className="mb-4 text-sm text-grey-900">
                Pick a start date, class time and time zone. Your child&apos;s
                spot is reserved for that day plus the next 6 days.
              </p>

              <label className="block mb-1 text-sm font-600">
                Preferred start date
              </label>
              <input
                type="date"
                min={minBookingDate()}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                // Open the date picker when clicking anywhere on the field, not
                // just on the calendar icon.
                onClick={(e) => {
                  const input = e.currentTarget as HTMLInputElement & {
                    showPicker?: () => void
                  }
                  input.showPicker?.()
                }}
                className="w-full px-3 py-2 mb-4 border rounded-lg cursor-pointer border-grey-400 focus:outline-none focus:border-orange-500"
              />

              <label className="block mb-1 text-sm font-600">Class time</label>
              <select
                value={classTime}
                onChange={(e) => setClassTime(e.target.value)}
                className="w-full px-3 py-2 mb-4 bg-white border rounded-lg cursor-pointer border-grey-400 focus:outline-none focus:border-orange-500 text-grey"
              >
                <option value="" disabled>
                  Select a time
                </option>
                {TIME_OPTIONS.map((slot) => (
                  <option key={slot.value} value={slot.value}>
                    {slot.label}
                  </option>
                ))}
              </select>

              <label className="block mb-1 text-sm font-600">Time zone</label>
              <select
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                className="w-full px-3 py-2 mb-4 bg-white border rounded-lg border-grey-400 focus:outline-none focus:border-orange-500"
              >
                {/* Keep the detected zone selectable even if absent from the list. */}
                {!TIMEZONE_OPTIONS.some((tz) => tz.value === timezone) && (
                  <option value={timezone}>{timezone}</option>
                )}
                {TIMEZONE_OPTIONS.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </select>

              {startDate && (
                <div className="p-3 mt-2 rounded-lg bg-grey-100">
                  <p className="text-xs text-grey-900">Selected</p>
                  <p className="font-700">{weekRange}</p>
                  <p className="text-sm font-600 text-orange">
                    {classTimeLabel} · {timezone}
                  </p>
                  <div className="flex gap-2 pt-2 mt-2 text-xs border-t border-grey-300 text-grey-800">
                    <span className="text-orange">★</span>
                    <span>
                      This is a tentative class schedule. You can update it
                      later if needed. One of our agents will connect with you
                      before onboarding to confirm the details.
                    </span>
                  </div>
                </div>
              )}

              {error && (
                <p className="mt-3 text-sm text-red-200 font-600">{error}</p>
              )}

              <Button
                className="w-full mt-6"
                loading={isLoggedIn && loading}
                disabled={!canProceedStep1 || loading}
                onClick={handleStep1Proceed}
              >
                {isLoggedIn ? 'Proceed to payment' : 'Proceed to next step'}
              </Button>
            </div>
          )}

          {/* STEP 2 - OTP login for an existing account. On success, the shared
              container logs the user in and runs createEnrolmentAndShowPayment,
              advancing to the payment step. */}
          {!isLoggedIn && step === 2 && showOtp && (
            <div>
              <h3 className="mb-2 text-lg font-700 text-center">
                Verify your phone
              </h3>
              <p className="mb-4 text-sm text-center text-grey-700">
                An account already exists for +{phone}. Enter the OTP to
                continue.
              </p>
              <VerifyPhoneOtpContainer
                phone={phone}
                source="/camp/"
                handleOtpVerified={createEnrolmentAndShowPayment}
              />
            </div>
          )}

          {/* STEP 2 - DETAILS (guests only; logged-in users skip this) */}
          {!isLoggedIn && step === 2 && !showOtp && (
            <div>
              <h3 className="mb-4 text-lg font-700 text-center">
                Enter your details
              </h3>

              <div className="mb-4">
                <CustomPhoneInput
                  id="camp-phone"
                  name="camp-phone"
                  label="WhatsApp phone number"
                  value={phone}
                  onChange={(value) => setPhone(value)}
                  countryCode={countryCode?.toLowerCase()}
                />
              </div>

              <div className="mb-2">
                {/* Native select so the dropdown overflows the modal and never
                  shifts the page layout when opened. */}
                <select
                  value={grade.value}
                  onChange={(e) =>
                    setGrade(
                      GRADES.find((g) => g.value === e.target.value) || {
                        value: '',
                        label: '',
                      }
                    )
                  }
                  className={`w-full px-4 py-2 bg-white border rounded cursor-pointer border-grey-500 focus:outline-none focus:border-orange-500 ${
                    grade.value ? 'text-grey' : 'text-grey-900'
                  }`}
                >
                  <option value="">Select grade/class</option>
                  {GRADES.map((g) => (
                    <option key={g.value} value={g.value}>
                      {g.label}
                    </option>
                  ))}
                </select>
              </div>

              <p className="mt-4 text-xs text-grey-800">
                By continuing, you agree to the Terms of Service and Privacy
                Policy, and that you have parental consent. Updates are sent via
                email, SMS &amp; WhatsApp; class reminders by call.
              </p>

              {error && (
                <p className="mt-3 text-sm text-red-200 font-600">{error}</p>
              )}

              <div className="flex gap-3 mt-6">
                <Button
                  variant="outline"
                  className="flex-none"
                  onClick={() => setStep(1)}
                >
                  Back
                </Button>
                <Button
                  className="flex-1"
                  loading={loading}
                  disabled={!canProceedStep2 || loading}
                  onClick={handleStep2Submit}
                >
                  Proceed to payment
                </Button>
              </div>
            </div>
          )}

          {/* PAYMENT STEP - booking summary + inline gateway options */}
          {step === paymentStep && (
            <div>
              <h3 className="mb-3 text-lg font-700">Review &amp; pay</h3>

              {/* Booking summary */}
              <div className="p-3 mb-4 border rounded-lg border-grey-300">
                <div className="flex justify-between py-1 text-sm">
                  <span className="text-grey-700">Course</span>
                  <span className="text-right font-600">
                    {course.curriculumCard?.title}
                  </span>
                </div>
                <div className="flex justify-between py-1 text-sm">
                  <span className="text-grey-700">Preferred start</span>
                  <span className="text-right font-600">
                    {startDateLabel}, {classTimeLabel}
                  </span>
                </div>
                <div className="flex justify-between py-1 text-sm">
                  <span className="text-grey-700">Time zone</span>
                  <span className="font-600">{timezone}</span>
                </div>
                <div className="flex justify-between py-1 text-sm">
                  <span className="text-grey-700">Sessions</span>
                  <span className="font-600">{course.sessions} sessions</span>
                </div>

                {priceInfo && (
                  <div className="flex items-baseline justify-between pt-2 mt-2 border-t border-grey-300">
                    <span className="font-700">Total</span>
                    <span className="text-right whitespace-nowrap">
                      <span className="text-lg font-800 text-orange">
                        {priceInfo.discounted}
                      </span>
                      {priceInfo.hasDiscount && (
                        <span className="ml-2 text-sm line-through text-grey-700">
                          {priceInfo.total}
                        </span>
                      )}
                    </span>
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-3">
                {/* CTA always reads "Proceed to payment" regardless of gateway.
                    Stripe uses a Checkout Session (checkout.stripe.com) for a
                    nicer flow with a redirect back; other gateways use their
                    stored link. Falls back to the hosted payment page when no
                    inline gateway links are available. */}
                {Object.keys(paymentLinks).length > 0
                  ? Object.entries(paymentLinks).map(([gateway, link]) =>
                      gateway === 'stripe' ? (
                        <Button
                          key={gateway}
                          className="w-full py-3 text-lg"
                          loading={checkoutLoading}
                          disabled={checkoutLoading}
                          onClick={handleStripeCheckout}
                        >
                          Proceed to payment
                        </Button>
                      ) : (
                        <a
                          key={gateway}
                          href={link}
                          className="w-full py-3 text-lg text-center text-white rounded btn bg-orange hover:bg-orange-500 focus:outline-none"
                        >
                          Proceed to payment
                        </a>
                      )
                    )
                  : paymentPageUrl && (
                      <a
                        href={paymentPageUrl}
                        className="w-full py-3 text-lg text-center text-white rounded btn bg-orange hover:bg-orange-500 focus:outline-none"
                      >
                        Proceed to payment
                      </a>
                    )}
              </div>

              <p className="mt-4 text-xs text-grey-700">
                You will be redirected to a secure payment page. Your spot is
                confirmed once payment is complete.
              </p>
            </div>
          )}
        </div>
      </Modal>
    </>
  )
}

export default BookingModal
